// @ts-nocheck
import { City } from './types';
import { 
  API_BASE_URL, 
  COUNTRY_CODE_ISO3_TO_ISO2, 
  COUNTRY_NAME_TO_CODE_MAP, 
  COUNTRY_CODE_ISO2_TO_ISO3,
  EU_COUNTRIES,
  NON_EU_EUROPEAN,
  EUROPE_ADJACENT,
  MAJOR_PORTS,
  REGION_CENTERS,
  LANDLOCKED_COUNTRIES
} from './constants';

/**
 * Search for cities using the backend geocode API
 * @param query Search query string
 * @param isNational Whether to search only national (Swiss) locations
 * @returns Promise with array of City objects
 */
export const searchCities = async (query: string, isNational: boolean = false): Promise<City[]> => {
  if (!query || query.length < 3) {
    return [];
  }

  try {
    // Use different endpoints for national vs international search
    const endpoint = isNational ? '/shipment/geocode/national' : '/shipment/geocode';
    const response = await fetch(`${API_BASE_URL}${endpoint}?q=${encodeURIComponent(query)}&lang=de-DE`);
    
    if (!response.ok) {
      console.error('Geocode API error:', response.status);
      return [];
    }

    const data = await response.json();
    console.log('API Response:', data);
    
    // Check if we have items in the response
    if (data && data.items && Array.isArray(data.items)) {
      // Transform the HERE API results to match our City interface
      return data.items.map((item: any, index: number) => {
        // The backend returns the data correctly structured
        const title = item.title || '';
        const addressLabel = item.addressLabel || item.displayLabel || title;
        const postalCode = item.postalCode || '';
        const city = item.city || '';
        const street = item.street || '';
        const country = item.country || '';
        const countryCode = item.countryCode || '';
        // Only default to CHE if we're doing a national search and countryCode is CH
        const countryCode3 = item.countryCode3 || (isNational && countryCode === 'CH' ? 'CHE' : COUNTRY_CODE_ISO2_TO_ISO3[countryCode] || '');
        const state = item.state || '';
        
        // Extract coordinates
        const lat = item.coords?.lat || null;
        const lng = item.coords?.lng || null;
        
        // Create a properly structured city object with all available data
        const cityObject = {
          id: index + 1,
          title: city || title,
          postalCode: postalCode,
          street: street,
          city: city,
          addressLabel,
          lat,
          lng,
          country: country,
          countryCode3: countryCode3,
          countryCode: countryCode,
          resultType: '', // Not needed for national search
          company_name: "",
          email: "",
          phone: "",
          additional_info: ""
        };
        
        console.log('Created city object:', cityObject);
        return cityObject;
      });
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching cities:', error);
    return [];
  }
};

/**
 * Determine which region a country belongs to
 * @param countryCode 2-letter country code
 * @returns Region identifier
 */
export const getRegion = (countryCode: string): string => {
  if (EU_COUNTRIES.includes(countryCode) || ['CH', 'NO', 'GB'].includes(countryCode)) {
    return 'westEurope';
  }
  if (NON_EU_EUROPEAN.includes(countryCode) && !['CH', 'NO', 'GB'].includes(countryCode)) {
    return 'eastEurope';
  }
  if (['MA', 'DZ', 'TN', 'LY', 'EG'].includes(countryCode)) {
    return 'northAfrica';
  }
  if (['SA', 'AE', 'QA', 'OM', 'BH', 'KW', 'IQ', 'IR', 'SY', 'JO', 'LB', 'IL'].includes(countryCode)) {
    return 'middleEast';
  }
  if (['IN', 'PK', 'BD', 'NP', 'LK', 'MV', 'AF'].includes(countryCode)) {
    return 'southAsia';
  }
  if (['CN', 'JP', 'KR', 'TW', 'HK', 'MO', 'MN', 'KP'].includes(countryCode)) {
    return 'eastAsia';
  }
  if (['AU', 'NZ', 'PG', 'FJ', 'SB', 'VU', 'NC', 'PF'].includes(countryCode)) {
    return 'oceania';
  }
  if (['US', 'CA', 'MX', 'GT', 'BZ', 'SV', 'HN', 'NI', 'CR', 'PA'].includes(countryCode)) {
    return 'northAmerica';
  }
  if (['BR', 'AR', 'CL', 'PE', 'CO', 'VE', 'EC', 'BO', 'PY', 'UY', 'GY', 'SR', 'GF'].includes(countryCode)) {
    return 'southAmerica';
  }
  // Default to central Africa for any other country
  return 'centralAfrica';
};


/**
 * Check if a country has coastal access
 * @param countryCode 2-letter country code
 * @returns Boolean indicating if country has coastal access
 */
export const hasCoastalAccess = (countryCode: string): boolean => {
  return !LANDLOCKED_COUNTRIES.includes(countryCode);
};

/**
 * Determine if land transport is available between two countries
 * @param pickupCountry Pickup country 2-letter code
 * @param deliveryCountry Delivery country 2-letter code
 * @param distance Approximate distance between countries
 * @returns Boolean indicating if land transport is available
 */
export const isLandAvailable = (
  pickupCountry: string, 
  deliveryCountry: string, 
  distance: number
): boolean => {
  // Land transport for close European countries
  if ((EU_COUNTRIES.includes(pickupCountry) && EU_COUNTRIES.includes(deliveryCountry)) ||
      (NON_EU_EUROPEAN.includes(pickupCountry) && NON_EU_EUROPEAN.includes(deliveryCountry))) {
    return true;
  }
  
  // Land transport between EU and non-EU European countries
  if ((EU_COUNTRIES.includes(pickupCountry) && NON_EU_EUROPEAN.includes(deliveryCountry)) ||
      (NON_EU_EUROPEAN.includes(pickupCountry) && EU_COUNTRIES.includes(deliveryCountry))) {
    return true;
  }
  
  // Land transport to adjacent countries
  if ((EU_COUNTRIES.includes(pickupCountry) && EUROPE_ADJACENT.includes(deliveryCountry)) ||
      (EUROPE_ADJACENT.includes(pickupCountry) && EU_COUNTRIES.includes(deliveryCountry)) ||
      (NON_EU_EUROPEAN.includes(pickupCountry) && EUROPE_ADJACENT.includes(deliveryCountry)) ||
      (EUROPE_ADJACENT.includes(pickupCountry) && NON_EU_EUROPEAN.includes(deliveryCountry))) {
    return true;
  }
  
  // Land generally not available for very long distances
  return distance < 2000;
};

/**
 * Determine if air transport is available between two countries
 * @returns Boolean indicating if air transport is available
 */
export const isAirAvailable = (): boolean => {
  // Air is almost always available except for very specific restricted routes
  return true;
};

/**
 * Determine if sea transport is available between two countries
 * @param pickupCountry Pickup country 2-letter code
 * @param deliveryCountry Delivery country 2-letter code
 * @param distance Approximate distance between countries
 * @returns Boolean indicating if sea transport is available
 */
export const isSeaAvailable = (
  pickupCountry: string, 
  deliveryCountry: string, 
  distance: number
): boolean => {
  // Special case for Switzerland - it's technically landlocked but sea freight
  // is commonly used via ports in neighboring countries
  const hasEffectiveSeaAccess = (country: string): boolean => {
    if (country === 'CH') return true; // Switzerland has effective sea access
    return !LANDLOCKED_COUNTRIES.includes(country);
  };
  
  // If both countries have coastal access (actual or effective)
  if (hasEffectiveSeaAccess(pickupCountry) && hasEffectiveSeaAccess(deliveryCountry)) {
    // For very short distances, sea might not be practical
    return distance > 500;
  }
  
  return false;
};

/**
 * Determine which shipping methods are available for a route
 * @param pickup Pickup city
 * @param delivery Delivery city
 * @returns Array of available shipping methods
 */
export const getAvailableShippingMethods = (pickup: City | null, delivery: City | null): string[] => {
  // If either location is not selected, return an empty array
  if (!pickup || !delivery) {
    return [];
  }
  
  // We'll use the 3-letter country codes from the API as they're more reliable
  const pickupCountry3 = pickup.countryCode3;
  const deliveryCountry3 = delivery.countryCode3;
  
  console.log('getAvailableShippingMethods - Pickup country3:', pickupCountry3, 'Delivery country3:', deliveryCountry3);
  
  // Both are Switzerland - no shipping method needed (domestic)
  if (pickupCountry3 === 'CHE' && deliveryCountry3 === 'CHE') {
    console.log('Domestic route (CHE-CHE) - no shipping methods');
    return [];
  }
  
  // For all international shipments
  const methods: string[] = [];
  
  // Air is always available for international
  methods.push('air');
  
  // Sea is always available for international
  methods.push('sea');
  
  // For land, convert to 2-letter codes for our existing country group logic
  const pickupCountry2 = COUNTRY_CODE_ISO3_TO_ISO2[pickupCountry3] || '';
  const deliveryCountry2 = COUNTRY_CODE_ISO3_TO_ISO2[deliveryCountry3] || '';
  
  // Only add land for nearby countries
  if (
    // Check if we have valid 2-letter codes first
    pickupCountry2 && deliveryCountry2 && (
      // European countries
      (EU_COUNTRIES.includes(pickupCountry2) && EU_COUNTRIES.includes(deliveryCountry2)) ||
      (NON_EU_EUROPEAN.includes(pickupCountry2) && (EU_COUNTRIES.includes(deliveryCountry2) || NON_EU_EUROPEAN.includes(deliveryCountry2))) ||
      // Adjacent countries
      (EU_COUNTRIES.includes(pickupCountry2) && EUROPE_ADJACENT.includes(deliveryCountry2)) ||
      (EUROPE_ADJACENT.includes(pickupCountry2) && EU_COUNTRIES.includes(deliveryCountry2)) 
      // General distance check - only if close enough
    )
  ) {
    methods.push('land');
  }
  
  console.log('Available shipping methods:', methods);
  return methods;
};